/**
 * Cookie consent helper. Reads/writes the `trex_consent` first-party cookie
 * and exposes a tiny event bus so the GTMLoader can react to changes without
 * a full React context tree.
 *
 *   trex_consent value (URL-encoded JSON):
 *   {
 *     v: <banner version>,   // bump = ask again
 *     ts: <epoch ms>,        // when the choice was made
 *     analytics: bool,
 *     marketing: bool,
 *     necessary: true,
 *   }
 */
const COOKIE_NAME = "trex_consent";
const ONE_YEAR = 60 * 60 * 24 * 365; // seconds

// The default (pre-choice) categories. Necessary is ALWAYS granted.
export const DEFAULT_DENY = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const listeners = new Set();

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(next) {
  for (const fn of listeners) {
    try { fn(next); } catch { /* no-op */ }
  }
}

export function readConsent() {
  if (typeof document === "undefined") return null;
  const raw = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((s) => s.startsWith(`${COOKIE_NAME}=`));
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(raw.split("=")[1]));
  } catch {
    return null;
  }
}

export function writeConsent(choice, bannerVersion) {
  const payload = {
    v: bannerVersion || 1,
    ts: Date.now(),
    necessary: true,
    analytics: !!choice.analytics,
    marketing: !!choice.marketing,
  };
  const encoded = encodeURIComponent(JSON.stringify(payload));
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=${encoded}; Max-Age=${ONE_YEAR}; Path=/; SameSite=Lax${secure}`;
  notify(payload);
  return payload;
}

export function clearConsent() {
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
  notify(null);
}

/** True when the visitor has a valid recorded choice for the current version. */
export function hasValidConsent(consent, bannerVersion) {
  if (!consent) return false;
  if (bannerVersion && Number(consent.v || 0) < Number(bannerVersion)) return false;
  return true;
}

/**
 * Convert a consent record into a Google Consent Mode v2 signal map.
 * https://developers.google.com/tag-platform/security/guides/consent
 */
export function toConsentModeV2(consent) {
  const analytics = consent && consent.analytics ? "granted" : "denied";
  const marketing = consent && consent.marketing ? "granted" : "denied";
  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: analytics,
    functionality_storage: "granted",
    personalization_storage: analytics,
    security_storage: "granted",
  };
}
